import React, { useEffect, useId, useRef, useState } from 'react';
import {
  LISTING_FEE_CENTS,
  LISTING_FEE_PREMIUM_CENTS,
  LISTING_FEE_PREMIUM_MIN_PRICE,
  LISTING_SIDE_COMMISSION_LABEL,
  formatDollars,
  listingFeeCents,
  listingSideCommission,
} from '../../utils/pricing';
import { LEGAL_DISCLAIMER } from '../../utils/disclaimers';
import { SERVICE_AREA_LABEL, setPublicPageMeta } from '../../utils/publicSite';
import { PublicLink } from './PublicLayout';
import WaitlistForm from './WaitlistForm';
import styles from './LandingPage.module.css';

const FEE = formatDollars(LISTING_FEE_CENTS);
const FEE_PREMIUM = formatDollars(LISTING_FEE_PREMIUM_CENTS);
const PREMIUM_THRESHOLD = `$${LISTING_FEE_PREMIUM_MIN_PRICE.toLocaleString('en-US')}`;

// The worked example used in the copy ("On a $350,000 home, 2.5% is $8,750").
const EXAMPLE_PRICE = 350_000;
const EXAMPLE_COMMISSION = formatDollars(listingSideCommission(EXAMPLE_PRICE) * 100);

const CUT_PRESETS = [250_000, 350_000, 500_000, 1_200_000];

const shortPrice = (price) => (price >= 1_000_000
  ? `$${(price / 1_000_000).toLocaleString('en-US', { maximumFractionDigits: 1 })}M`
  : `$${Math.round(price / 1000)}k`);

/**
 * The hero's signature: a receipt where the listing commission gets cut.
 * Listing side only, never the combined 5–6% (see utils/pricing.js).
 */
function CommissionCut() {
  const [price, setPrice] = useState(EXAMPLE_PRICE);
  const commission = listingSideCommission(price);
  const fee = listingFeeCents(price) / 100;
  const kept = commission - fee;

  return (
    <figure className={styles.receipt} aria-labelledby="receipt-caption" data-testid="commission-cut">
      <div className={styles.receiptHead}>
        <span className={styles.receiptBrand}>cutthe6.com</span>
        <span className={styles.receiptTag}>Seller&apos;s side</span>
      </div>
      <figcaption id="receipt-caption" className={styles.receiptCaption}>
        Selling a <strong>{formatDollars(price * 100)}</strong> home
      </figcaption>
      <div className={styles.receiptPresets} role="group" aria-label="Example home price">
        {CUT_PRESETS.map((preset) => (
          <button
            key={preset}
            type="button"
            className={styles.receiptPreset}
            aria-pressed={price === preset}
            onClick={() => setPrice(preset)}
          >
            {shortPrice(preset)}
          </button>
        ))}
      </div>
      <dl className={styles.receiptLines}>
        <div className={`${styles.receiptLine} ${styles.receiptLineCut}`}>
          <dt>Listing agent at {LISTING_SIDE_COMMISSION_LABEL}</dt>
          <dd>
            <span className={styles.struck} key={price} data-testid="landing-commission">
              {formatDollars(commission * 100)}
            </span>
          </dd>
        </div>
        <div className={`${styles.receiptLine} ${styles.receiptLineOurs}`}>
          <dt>cutthe6.com flat fee</dt>
          <dd data-testid="landing-fee">{formatDollars(fee * 100)}</dd>
        </div>
        <div className={`${styles.receiptLine} ${styles.receiptLineKept}`}>
          <dt>Stays with you</dt>
          <dd data-testid="landing-kept">{formatDollars(kept * 100)}</dd>
        </div>
      </dl>
      <p className={styles.receiptNote}>
        Illustrative. Listing commissions vary and are negotiable, and a listing agent provides
        services we don&apos;t. Any buyer&apos;s-agent pay is separate and not included.
      </p>
    </figure>
  );
}

const STEPS = {
  sell: [
    {
      title: 'List it',
      body: `Enter your address and confirm you own the home. Add photos and details, complete Wisconsin's seller disclosure report, sign our seller agreement, and pay the flat fee when you publish.`,
    },
    {
      title: 'Show it and take offers',
      body: 'Buyers request tours at times you make available. Offers arrive in one inbox, where you accept, counter or decline. AI suggestions are there if you want them; every decision is yours.',
    },
    {
      title: 'Get to closing',
      body: 'Track contingencies and deadlines, e-sign the paperwork, and work through the closing checklist with the title company or attorney you choose.',
    },
  ],
  buy: [
    {
      title: 'Find a home',
      body: 'Create a free account to browse homes listed on cutthe6.com, save searches and favorites, and estimate what you can afford.',
    },
    {
      title: 'Tour and make an offer',
      body: 'Request a tour, then write, e-sign and track your offer in the app. Making an offer on a cutthe6.com home is free.',
    },
    {
      title: 'Follow it to closing',
      body: 'See every contingency, deadline and document in one place, from accepted offer to closing day.',
    },
  ],
};

function HowItWorks({ tab, onTabChange }) {
  const baseId = useId();
  const tabs = [
    { key: 'sell', label: 'Selling' },
    { key: 'buy', label: 'Buying' },
  ];
  const onKeyDown = (event) => {
    if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
    event.preventDefault();
    const next = tab === 'sell' ? 'buy' : 'sell';
    onTabChange(next);
    document.getElementById(`${baseId}-tab-${next}`)?.focus();
  };

  return (
    <section id="how-it-works" className={styles.how} aria-labelledby="how-heading">
      <div className={styles.sectionInner}>
        <div className={styles.howHeader}>
          <h2 id="how-heading" className={styles.sectionTitle}>How it works</h2>
          <div className={styles.howTabs} role="tablist" aria-label="How it works for">
            {tabs.map((item) => (
              <button
                key={item.key}
                id={`${baseId}-tab-${item.key}`}
                type="button"
                role="tab"
                aria-selected={tab === item.key}
                aria-controls={`${baseId}-panel`}
                tabIndex={tab === item.key ? 0 : -1}
                className={styles.howTab}
                onClick={() => onTabChange(item.key)}
                onKeyDown={onKeyDown}
              >
                {item.label}
              </button>
            ))}
          </div>
        </div>
        <ol
          id={`${baseId}-panel`}
          role="tabpanel"
          aria-labelledby={`${baseId}-tab-${tab}`}
          className={styles.steps}
          key={tab}
        >
          {STEPS[tab].map((step, index) => (
            <li key={step.title} className={styles.step} style={{ '--step-index': index }}>
              <span className={styles.stepNumber} aria-hidden="true">{String(index + 1).padStart(2, '0')}</span>
              <h3 className={styles.stepTitle}>{step.title}</h3>
              <p className={styles.stepBody}>{step.body}</p>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

const FAQ = [
  {
    q: 'Is cutthe6.com a real estate agent or broker?',
    a: "No. cutthe6.com is software. We're not a licensed broker or a law firm, we don't represent you or negotiate for you, and we don't give legal, tax or appraisal advice. You run your own sale, and you can hire a real estate attorney, title company or licensed agent whenever you like.",
  },
  {
    q: 'What does it cost?',
    a: `Buyers pay nothing to browse, tour and make offers on cutthe6.com homes. Sellers pay one flat software fee when they publish: ${FEE}, or ${FEE_PREMIUM} for homes listed at ${PREMIUM_THRESHOLD} or more. It isn't a commission, it isn't taken from your sale proceeds, and it's owed whether or not your home sells.`,
  },
  {
    q: 'How much will I actually save?',
    a: `It depends on what you would otherwise pay a listing agent. Listing-side commissions are commonly around 2.5–3% of the price; on a ${formatDollars(EXAMPLE_PRICE * 100)} home, ${LISTING_SIDE_COMMISSION_LABEL} is ${EXAMPLE_COMMISSION}. Commissions vary and are negotiable, so treat any comparison as an estimate.`,
  },
  {
    q: "Do I still pay a buyer's agent?",
    a: "Not through us. Our fee covers your use of the software only. If your buyer has an agent, the buyer's agreement with that agent sets the agent's pay. The buyer may ask you to cover some or all of it as a term of the offer, and you can accept, counter or decline it like any other term.",
  },
  {
    q: 'Will my home show up on Zillow, Redfin or the MLS?',
    a: "Not today. Your listing appears on cutthe6.com, and you can share its link anywhere you like. We don't send listings to the MLS or other home-search sites yet.",
  },
  {
    q: 'Which states do you serve?',
    a: `${SERVICE_AREA_LABEL} only for now, because offers, disclosures and contingencies follow state law. If you're buying or selling somewhere else, join the waitlist and we'll let you know when we launch in your state.`,
  },
  {
    q: 'Who handles the closing?',
    a: 'A title company or real estate attorney that you choose. cutthe6.com keeps the checklist, deadlines and signed documents in one place so both sides can see what is left to do.',
  },
  {
    q: 'Does the AI make decisions for me?',
    a: "No. It suggests. It can summarize market data and comparable sales and offer ideas for a price, an offer or a counter-offer. It isn't an appraisal or legal advice, and you decide every term.",
  },
];

/**
 * Public home page for signed-out visitors: Buy / Sell split, how it works,
 * the flat fee (framed against the listing-side commission only), the
 * Wisconsin-only service area with a waitlist, and an FAQ. Rendered inside
 * PublicLayout by App.js.
 *
 * @param {object} props
 * @param {(path: string) => void} props.onNavigate - Move to another public path.
 * @param {() => void} props.onSignUp - Create an account (buyer by default).
 * @param {() => void} props.onSignIn
 * @param {string|null} [props.section] - #section to bring into view ('faq', 'waitlist', 'how-it-works', 'how-buying', 'how-selling').
 * @param {number} [props.sectionNonce] - Changes on every in-page jump, so the same section can be re-scrolled.
 */
export default function LandingPage({ onNavigate, onSignUp, onSignIn, section = null, sectionNonce = 0 }) {
  const [howTab, setHowTab] = useState(section === 'how-buying' ? 'buy' : 'sell');
  const firstScroll = useRef(true);

  useEffect(() => {
    setPublicPageMeta({
      title: `Sell your own ${SERVICE_AREA_LABEL} home for a flat fee from ${FEE} | cutthe6.com`,
      description: `List, show, negotiate and close your own home sale in ${SERVICE_AREA_LABEL} with one flat software fee, from ${FEE}, instead of a listing commission. Buyers make offers on cutthe6.com homes for free.`,
      url: `${window.location.origin}/`,
    });
  }, []);

  useEffect(() => {
    const instant = firstScroll.current;
    firstScroll.current = false;
    if (!section) {
      if (!instant) window.scrollTo({ top: 0 });
      return;
    }
    if (section === 'how-buying') setHowTab('buy');
    if (section === 'how-selling') setHowTab('sell');
    const id = section.startsWith('how-') ? 'how-it-works' : section;
    const el = document.getElementById(id);
    if (el && typeof el.scrollIntoView === 'function') {
      el.scrollIntoView({ behavior: instant ? 'auto' : 'smooth', block: 'start' });
    }
  }, [section, sectionNonce]);

  return (
    <div className={styles.page}>
      {/* ── Hero ── */}
      <section className={styles.hero} aria-labelledby="hero-heading">
        <div className={styles.heroBackdrop} aria-hidden="true">
          <span className={styles.heroSix}>6</span>
          <span className={styles.heroSlash} />
        </div>
        <div className={styles.heroInner}>
          <div className={styles.heroCopy}>
            <p className={styles.eyebrow}>
              <span className={styles.eyebrowDot} aria-hidden="true" />
              {SERVICE_AREA_LABEL} homes · Owner-run sales
            </p>
            <h1 id="hero-heading" className={styles.heroTitle}>
              Sell your own home for a flat {FEE}.
              <em className={styles.heroTitleAccent}> Not a percentage.</em>
            </h1>
            <p className={styles.heroLead}>
              cutthe6.com is software for selling your own home in {SERVICE_AREA_LABEL}: list it, show it,
              take offers and get to closing, with the paperwork, deadlines and e-signatures in one place.
              The fee is {FEE}, or {FEE_PREMIUM} for homes listed at {PREMIUM_THRESHOLD} or more. Buyers tour
              and make offers on cutthe6.com homes for free.
            </p>
            <div className={styles.heroActions}>
              <PublicLink to="/sell" onNavigate={onNavigate} className={styles.btnPrimary} data-testid="hero-start-listing">
                Start your listing
              </PublicLink>
              <button type="button" className={styles.btnOutline} onClick={onSignUp}>
                I&apos;m buying
              </button>
            </div>
            <p className={styles.heroSignIn}>
              Have an account?{' '}
              <button type="button" className={styles.inlineLink} onClick={onSignIn}>Sign in</button>
            </p>
          </div>
          <CommissionCut />
        </div>
      </section>

      {/* ── Buy / Sell split ── */}
      <section className={styles.doors} aria-labelledby="doors-heading">
        <div className={styles.sectionInner}>
          <h2 id="doors-heading" className={styles.sectionTitle}>Which side are you on?</h2>
          <div className={styles.doorGrid}>
            <article className={`${styles.door} ${styles.doorSell}`} data-testid="door-sell">
              <span className={styles.doorWord} aria-hidden="true">Sell</span>
              <h3 className={styles.doorTitle}>Selling a home</h3>
              <p className={styles.doorPrice}>
                <strong>{FEE}</strong> flat, or {FEE_PREMIUM} for homes listed at {PREMIUM_THRESHOLD}+
              </p>
              <p className={styles.doorBody}>
                Publish your listing, set your showing times, and review every offer in one inbox. You set the
                price and the terms.
              </p>
              <div className={styles.doorActions}>
                <PublicLink to="/sell" onNavigate={onNavigate} className={styles.btnLight}>
                  Start your listing
                </PublicLink>
                <PublicLink to="/pricing" onNavigate={onNavigate} className={styles.doorLink}>
                  See pricing
                </PublicLink>
              </div>
            </article>
            <article className={`${styles.door} ${styles.doorBuy}`} data-testid="door-buy">
              <span className={styles.doorWord} aria-hidden="true">Buy</span>
              <h3 className={styles.doorTitle}>Buying a home</h3>
              <p className={styles.doorPrice}>
                <strong>$0</strong> to browse, tour and make offers on cutthe6.com homes
              </p>
              <p className={styles.doorBody}>
                Deal directly with the owner. Request tours, write and e-sign your offer, and follow every
                deadline through closing. No credit card needed.
              </p>
              <div className={styles.doorActions}>
                <button type="button" className={styles.btnDark} onClick={onSignUp}>
                  Create a free account
                </button>
                <PublicLink to="/#how-buying" onNavigate={onNavigate} className={styles.doorLink}>
                  How buying works
                </PublicLink>
              </div>
            </article>
          </div>
        </div>
      </section>

      <HowItWorks tab={howTab} onTabChange={setHowTab} />

      {/* ── Price & savings ── */}
      <section id="pricing-summary" className={styles.price} aria-labelledby="price-heading">
        <div className={`${styles.sectionInner} ${styles.priceGrid}`}>
          <div>
            <h2 id="price-heading" className={styles.sectionTitle}>One flat fee. No percentage.</h2>
            <ul className={styles.tickets}>
              <li className={styles.ticket}>
                <span className={styles.ticketAmount}>{FEE}</span>
                <span className={styles.ticketLabel}>Homes listed under {PREMIUM_THRESHOLD}</span>
              </li>
              <li className={styles.ticket}>
                <span className={styles.ticketAmount}>{FEE_PREMIUM}</span>
                <span className={styles.ticketLabel}>Homes listed at {PREMIUM_THRESHOLD} or more</span>
              </li>
              <li className={`${styles.ticket} ${styles.ticketBuyer}`}>
                <span className={styles.ticketAmount}>$0</span>
                <span className={styles.ticketLabel}>Buyers, on cutthe6.com homes</span>
              </li>
            </ul>
            <p className={styles.priceTerms}>
              Charged once, when you publish your listing. It&apos;s a fee for using the software: not a
              commission, not taken from your sale proceeds, and owed whether or not your home sells.
            </p>
            <PublicLink to="/pricing" onNavigate={onNavigate} className={styles.arrowLink}>
              See full pricing <span aria-hidden="true">→</span>
            </PublicLink>
          </div>
          <aside className={styles.savings} aria-labelledby="savings-heading">
            <h3 id="savings-heading" className={styles.savingsTitle}>What you&apos;re really saving</h3>
            <p>
              A traditional listing agent typically charges around 2.5–3% of the sale price to represent the
              seller. When you sell your own home, you don&apos;t pay that commission; you pay our flat software
              fee instead. On a {formatDollars(EXAMPLE_PRICE * 100)} home, {LISTING_SIDE_COMMISSION_LABEL} is
              {' '}{EXAMPLE_COMMISSION}.
            </p>
            <p>
              The 5–6% figure you often hear includes the buyer&apos;s agent. If your buyer has an agent, their
              agreement with that agent sets the pay, and the buyer may ask you to cover some or all of it in
              the offer. Our fee doesn&apos;t cover it, so we don&apos;t count it as savings.
            </p>
          </aside>
        </div>
      </section>

      {/* ── Service area + waitlist ── */}
      <section id="waitlist" className={styles.waitlist} aria-labelledby="waitlist-heading">
        <div className={`${styles.sectionInner} ${styles.waitlistGrid}`}>
          <div>
            <h2 id="waitlist-heading" className={styles.sectionTitle}>{SERVICE_AREA_LABEL} only, for now.</h2>
            <p className={styles.waitlistLead}>
              Offers, disclosures and deadlines follow state law, so we&apos;re building one state at a time,
              starting with {SERVICE_AREA_LABEL}. Tell us where you&apos;re buying or selling and we&apos;ll let you
              know when cutthe6.com opens there.
            </p>
          </div>
          <WaitlistForm />
        </div>
      </section>

      {/* ── FAQ ── */}
      <section id="faq" className={styles.faq} aria-labelledby="faq-heading">
        <div className={styles.sectionInner}>
          <h2 id="faq-heading" className={styles.sectionTitle}>Questions, answered</h2>
          <div className={styles.faqList}>
            {FAQ.map((item) => (
              <details key={item.q} className={styles.faqItem}>
                <summary className={styles.faqQuestion}>
                  <span>{item.q}</span>
                  <span className={styles.faqIcon} aria-hidden="true" />
                </summary>
                <p className={styles.faqAnswer}>{item.a}</p>
              </details>
            ))}
          </div>
          <p className={styles.faqDisclaimer} data-testid="faq-legal-disclaimer">{LEGAL_DISCLAIMER}</p>
        </div>
      </section>

      {/* ── Closing CTA ── */}
      <section className={styles.closer} aria-labelledby="closer-heading">
        <div className={styles.sectionInner}>
          <h2 id="closer-heading" className={styles.closerTitle}>Ready when you are.</h2>
          <div className={styles.heroActions}>
            <PublicLink to="/sell" onNavigate={onNavigate} className={styles.btnPrimary}>
              Start your listing
            </PublicLink>
            <button type="button" className={styles.btnOutlineLight} onClick={onSignUp}>
              Create a buyer account
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}
